import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { Injectable, Signal } from '@angular/core';
import { tapResponse } from '@ngrx/operators';
import { Observable, switchMap } from 'rxjs';
import { filter, map } from 'rxjs/operators';
import { API_CACHE_MODE_HEADER, EApiCacheMode } from '../../../core/interceptors/api-interceptor.interceptor';
import { IBreadcrumbNode } from '../../../shared/components/breadcrumb/breadcrumb.component';
import { BaseDatatableStore, IBaseDatatableState } from '../../../state/base-datatable-store.service';
import { ELoadStatus } from '../../../state/state.interface';
import { IUser } from '../../../state/user/user.interface';
import {
  HttpUtilitiesService,
  IBaseCrudResponse,
  IBaseResponse,
  IGenericCrudRequestConstructionParameters,
  IGetManyResponse,
} from '../../../utilities/http-utilities.service';
import { ITeam } from '../users-teams/teams/teams.interface';
import {
  IBoardTeamAssignmentBase,
  IBoardUserAssignment,
  IBoardUserAssignmentUserJoin,
  ICloneDashboard,
  IDashboard,
  IDashboardBulkEditRequest,
  IEditDashboard,
  IWorkspace,
  TAddDashboard,
} from './dashboards.interface';

export interface IDashboardState extends IBaseDatatableState<IDashboard> {
  adminOptions: IBoardUserAssignmentUserJoin[];
  adminOptionsLoading: ELoadStatus;
  teamOptions: ITeam[];
  teamOptionsLoading: ELoadStatus;
  userOptions: IBoardUserAssignmentUserJoin[];
  userOptionsLoading: ELoadStatus;
  workspaces: IWorkspace[];
  workspacesLoading: ELoadStatus;
  parents: IDashboard[];
  parentsLoading: ELoadStatus;
}

@Injectable()
export class DashboardsStore extends BaseDatatableStore<IDashboard, IDashboardState> {
  public readonly userOptions: Signal<IBoardUserAssignmentUserJoin[]> = this.selectSignal(
    (state: IDashboardState) => state.userOptions,
  );

  public readonly adminOptions: Signal<IBoardUserAssignmentUserJoin[]> = this.selectSignal(
    (state: IDashboardState) => state.adminOptions,
  );

  public readonly workspaces: Signal<IWorkspace[]> = this.selectSignal((state: IDashboardState) => state.workspaces);

  public readonly teamOptions: Signal<ITeam[]> = this.selectSignal((state: IDashboardState) => state.teamOptions);

  public readonly parentsOptions: Signal<IDashboard[]> = this.selectSignal((state: IDashboardState) => state.parents);

  public readonly userOptionsLoading$: Observable<ELoadStatus> = this.select(
    (state: IDashboardState) => state.userOptionsLoading,
  );

  public readonly adminOptionsLoading$: Observable<ELoadStatus> = this.select(
    (state: IDashboardState) => state.adminOptionsLoading,
  );

  public readonly teamOptionsLoading$: Observable<ELoadStatus> = this.select(
    (state: IDashboardState) => state.teamOptionsLoading,
  );

  public readonly workspacesLoading$: Observable<ELoadStatus> = this.select(
    (state: IDashboardState) => state.workspacesLoading,
  );

  public readonly parentsLoading$: Observable<ELoadStatus> = this.select(
    (state: IDashboardState) => state.parentsLoading,
  );

  public readonly afterCrudSuccess$ = this.select(
    (state: IBaseDatatableState<IDashboard>) => state.singleCrudLoading,
  ).pipe(filter((value: ELoadStatus) => value === ELoadStatus.success));

  readonly createOne = this.effect((trigger$: Observable<TAddDashboard>) =>
    trigger$.pipe(
      switchMap((item: TAddDashboard) => {
        this.patchState({ singleCrudLoading: ELoadStatus.loading });

        return this.createItem(item).pipe(
          tapResponse(
            (): void => {
              this.patchState({ singleCrudLoading: ELoadStatus.success });
            },
            // eslint-disable-next-line no-console,sonarjs/no-duplicate-string
            (error) => console.error('Error loading data', error),
          ),
        );
      }),
    ),
  );

  readonly updateOne = this.effect((trigger$: Observable<IEditDashboard>) =>
    trigger$.pipe(
      switchMap((item: IEditDashboard) => {
        this.patchState({ singleCrudLoading: ELoadStatus.loading });

        return this.updateItem(item).pipe(
          tapResponse(
            () => this.patchState({ singleCrudLoading: ELoadStatus.success }),
            // eslint-disable-next-line no-console
            (error) => console.error('Error loading data', error),
          ),
        );
      }),
    ),
  );

  override readonly deleteOne = this.effect((trigger$: Observable<number>) =>
    trigger$.pipe(
      switchMap((id: number) => {
        this.patchState({ singleCrudLoading: ELoadStatus.loading });

        return this.deleteItem(id).pipe(
          tapResponse(
            () => this.patchState({ singleCrudLoading: ELoadStatus.success }),
            // eslint-disable-next-line no-console
            (error) => console.error('Error loading data', error),
          ),
        );
      }),
    ),
  );

  override readonly deleteBulk = this.effect((trigger$: Observable<number[]>) =>
    trigger$.pipe(
      switchMap((itemIds: number[]) => {
        this.patchState({ bulkCrudLoading: ELoadStatus.loading });

        return this.deleteItems(itemIds).pipe(
          tapResponse(
            (response): void => {
              this.patchState({ bulkCrudLoading: ELoadStatus.success, bulkOperationFailedData: response.data });
            },
            // eslint-disable-next-line no-console
            (error) => console.error('Error loading data', error),
          ),
        );
      }),
    ),
  );

  readonly bulkUpdate = this.effect((trigger$: Observable<IDashboardBulkEditRequest[]>) =>
    trigger$.pipe(
      switchMap((payload) => {
        this.patchState({ bulkCrudLoading: ELoadStatus.loading });

        return this.updateDashboards(payload).pipe(
          tapResponse(
            (response): void => {
              this.patchState({ bulkCrudLoading: ELoadStatus.success, bulkOperationFailedData: response.data });
            },
            // eslint-disable-next-line no-console
            (error) => console.error('Error loading data', error),
          ),
        );
      }),
    ),
  );

  readonly clone = this.effect((trigger$: Observable<ICloneDashboard>) =>
    trigger$.pipe(
      switchMap((item: ICloneDashboard) => {
        this.patchState({ singleCrudLoading: ELoadStatus.loading });

        return this.cloneDashboard(item).pipe(
          tapResponse(
            (): void => {
              this.patchState({ singleCrudLoading: ELoadStatus.success });
            },
            // eslint-disable-next-line no-console,sonarjs/no-duplicate-string
            (error) => console.error('Error loading data', error),
          ),
        );
      }),
    ),
  );

  readonly loadItems = this.effect((trigger$: Observable<IGenericCrudRequestConstructionParameters>) =>
    trigger$.pipe(
      switchMap((params: IGenericCrudRequestConstructionParameters) => {
        const httpParams: HttpParams = this.httpUtilities.insertGenericCrudRequestParameters(params);
        this.patchState({ dataLoading: ELoadStatus.loading });

        return this.getItems(httpParams).pipe(
          tapResponse(
            (response: IGetManyResponse<IDashboard>) =>
              this.patchState({ data: response.data, dataLoading: ELoadStatus.success, dataTotal: response.total }),
            // eslint-disable-next-line no-console
            (error) => console.error('Error loading data', error),
          ),
        );
      }),
    ),
  );

  readonly loadAdminOptions = this.effect(
    (
      input$: Observable<{
        params: IGenericCrudRequestConstructionParameters;
        workspaceId: number;
      }>,
    ) =>
      input$.pipe(
        switchMap(({ params, workspaceId }) => {
          this.patchState({ adminOptionsLoading: ELoadStatus.loading });

          const httpParams = this.httpUtilities
            .insertGenericCrudRequestParameters(params)
            .set('workspaceId', workspaceId);

          return this.getUserDropdownOptions(httpParams).pipe(
            tapResponse(
              (output: IGetManyResponse<IUser>): void => {
                this.patchState({
                  adminOptions: output.data,
                  adminOptionsLoading: ELoadStatus.success,
                });
              },
              (error) => console.error('Error loading data', error),
            ),
          );
        }),
      ),
  );

  readonly loadUserOptions = this.effect(
    (
      input$: Observable<{
        params: IGenericCrudRequestConstructionParameters;
        workspaceId: number;
      }>,
    ) =>
      input$.pipe(
        switchMap(({ params, workspaceId }) => {
          this.patchState({ userOptionsLoading: ELoadStatus.loading });

          const httpParams = this.httpUtilities
            .insertGenericCrudRequestParameters(params)
            .set('workspaceId', workspaceId);

          return this.getUserDropdownOptions(httpParams).pipe(
            tapResponse(
              (output: IGetManyResponse<IUser>): void => {
                this.patchState({
                  userOptions: output.data,
                  userOptionsLoading: ELoadStatus.success,
                });
              },
              (error) => console.error('Error loading data', error),
            ),
          );
        }),
      ),
  );

  readonly loadTeamOptions = this.effect((trigger$: Observable<IGenericCrudRequestConstructionParameters>) =>
    trigger$.pipe(
      switchMap((params: IGenericCrudRequestConstructionParameters) => {
        this.patchState({ teamOptionsLoading: ELoadStatus.loading });
        const httpParams: HttpParams = this.httpUtilities.insertGenericCrudRequestParameters(params);

        return this.getTeamDropdownOptions(httpParams).pipe(
          tapResponse(
            (output: IGetManyResponse<ITeam>): void => {
              this.patchState({
                teamOptions: output.data,
                teamOptionsLoading: ELoadStatus.success,
              });
            },
            // eslint-disable-next-line no-console
            (error) => console.error('Error loading data', error),
          ),
        );
      }),
    ),
  );

  readonly loadWorkspaces = this.effect((trigger$: Observable<IGenericCrudRequestConstructionParameters>) =>
    trigger$.pipe(
      switchMap((params: IGenericCrudRequestConstructionParameters) => {
        this.patchState({ workspacesLoading: ELoadStatus.loading });
        const httpParams: HttpParams = this.httpUtilities.insertGenericCrudRequestParameters(params);

        return this.getWorkspaces(httpParams).pipe(
          tapResponse(
            (output: IGetManyResponse<IWorkspace>): void => {
              this.patchState({
                workspaces: output.data,
                workspacesLoading: ELoadStatus.success,
              });
            },
            // eslint-disable-next-line no-console
            (error) => console.error('Error loading data', error),
          ),
        );
      }),
    ),
  );

  readonly loadParents = this.effect((trigger$: Observable<IGenericCrudRequestConstructionParameters>) =>
    trigger$.pipe(
      switchMap((params: IGenericCrudRequestConstructionParameters) => {
        this.patchState({ parentsLoading: ELoadStatus.loading });
        const httpParams: HttpParams = this.httpUtilities.insertGenericCrudRequestParameters(params);

        return this.getDashboards(httpParams).pipe(
          tapResponse(
            (output: IGetManyResponse<IDashboard>): void => {
              this.patchState({
                parents: output.data,
                parentsLoading: ELoadStatus.success,
              });
            },
            // eslint-disable-next-line no-console
            (error) => console.error('Error loading data', error),
          ),
        );
      }),
    ),
  );

  constructor(
    private readonly http: HttpClient,
    private readonly httpUtilities: HttpUtilitiesService,
  ) {
    super({
      adminOptions: [],
      adminOptionsLoading: ELoadStatus.initial,
      bulkCrudLoading: ELoadStatus.initial,
      bulkOperationFailedData: [],
      data: [],
      dataLoading: ELoadStatus.initial,
      dataTotal: 0,
      parents: [],
      parentsLoading: ELoadStatus.initial,
      singleCrudLoading: ELoadStatus.initial,
      teamOptions: [],
      teamOptionsLoading: ELoadStatus.initial,
      userOptions: [],
      userOptionsLoading: ELoadStatus.initial,
      workspaces: [],
      workspacesLoading: ELoadStatus.initial,
    });
  }

  private createItem(body: TAddDashboard): Observable<IBaseResponse<IDashboard>> {
    return this.http.post<IBaseResponse<IDashboard>>('dashboards', body);
  }

  private updateItem(editDashboard: IEditDashboard): Observable<IBaseResponse<IDashboard>> {
    return this.http.patch<IBaseResponse<IDashboard>>(`dashboards/${editDashboard.id}`, editDashboard.dto);
  }

  private deleteItem(id: number): Observable<IBaseResponse<IDashboard>> {
    return this.http.delete<IBaseResponse<IDashboard>>(`dashboards/${id}`);
  }

  private getItems(params: HttpParams): Observable<IGetManyResponse<IDashboard>> {
    return this.http.get<IGetManyResponse<IDashboard>>('dashboards', {
      headers: new HttpHeaders({ [API_CACHE_MODE_HEADER]: EApiCacheMode.none }),
      params,
    });
  }

  public getAncestorChain(dashboardId: number): Observable<IBreadcrumbNode[]> {
    return this.http
      .get<IBaseResponse<IBreadcrumbNode[]>>(`dashboards/${dashboardId}/breadcrumb`)
      .pipe(map((response: IBaseResponse<IBreadcrumbNode[]>) => response.data ?? []));
  }

  private deleteItems(ids: number[]): Observable<IBaseResponse<IBaseCrudResponse[]>> {
    return this.http.delete<IBaseResponse<IBaseCrudResponse[]>>('dashboards/bulk/delete', {
      body: { payload: ids },
    });
  }

  private updateDashboards(payload: IDashboardBulkEditRequest[]): Observable<IBaseResponse<IBaseCrudResponse[]>> {
    return this.http.patch<IBaseResponse<IBaseCrudResponse[]>>('dashboards/bulk/update', { payload });
  }

  private getUserDropdownOptions(params: HttpParams): Observable<IGetManyResponse<IUser>> {
    return this.http.get<IGetManyResponse<IUser>>('users', { params });
  }

  private getTeamDropdownOptions(params: HttpParams): Observable<IGetManyResponse<ITeam>> {
    return this.http.get<IGetManyResponse<ITeam>>('teams', { params });
  }

  private getUserAssignments(params: HttpParams): Observable<IGetManyResponse<IBoardUserAssignment>> {
    return this.http.get<IGetManyResponse<IBoardUserAssignment>>('dashboard-user-assignments', { params });
  }

  private getTeamAssignments(params: HttpParams): Observable<IGetManyResponse<IBoardTeamAssignmentBase>> {
    return this.http.get<IGetManyResponse<IBoardTeamAssignmentBase>>('dashboard-team-assignments', { params });
  }

  private getWorkspaces(params: HttpParams): Observable<IGetManyResponse<IWorkspace>> {
    return this.http.get<IGetManyResponse<IWorkspace>>('workspaces', { params });
  }

  private getDashboards(params: HttpParams): Observable<IGetManyResponse<IDashboard>> {
    return this.http.get<IGetManyResponse<IDashboard>>('dashboards', { params });
  }

  private cloneDashboard(cloneDashboard: ICloneDashboard): Observable<IBaseResponse<IDashboard>> {
    return this.http.post<IBaseResponse<IDashboard>>(`dashboards/clone-dashboard/${cloneDashboard.id}`, {
      name: cloneDashboard.name,
    });
  }
}
